import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';

import { ApiClient } from '../../../api-client';
import { useFetchUserForAuthentication } from '../api';
import { isLoggedInAtom } from '../atoms';
import { CoreAuthProviderProps } from '../types';
import { checkIfHasSessionRefreshToken, removeLegacySessionFromStorage } from '../utils';

/**
 * This provider is the core authentication provider for the application.
 * It is used to fetch the auth state and the user data from the core API.
 * It is used to fetch the auth state and the user data from the core API before it renders the application.
 */
const CoreAuthWebProvider = ({
  children,
  showLoader = true,
  LoaderComponent,
  onRefreshTokenFailure,
}: CoreAuthProviderProps) => {
  const queryClient = useQueryClient();

  const hasFetchedUser = useRef(false);

  const [hasCheckedAuthentication, setHasCheckedAuthentication] = useState(false);

  const { mutateAsync: fetchUserAsync } = useFetchUserForAuthentication();

  const fetchUser = useCallback(async () => {
    return await fetchUserAsync();
  }, [fetchUserAsync]);

  const handleCheckedAuth = useCallback((isLoggedIn: boolean) => {
    const defaultStore = getDefaultStore();
    defaultStore.set(isLoggedInAtom, isLoggedIn);
    setHasCheckedAuthentication(true);
  }, []);

  useEffect(() => {
    const handleValidateSession = async () => {
      try {
        const response = await queryClient.fetchQuery({
          queryKey: ['user'],
          queryFn: fetchUser,
        });
        if (!response?.uuid) {
          throw new Error('No user uuid found');
        }
        return handleCheckedAuth(true);
      } catch {
        return handleCheckedAuth(false);
      }
    };

    if (!hasFetchedUser.current) {
      const hasRefreshToken = checkIfHasSessionRefreshToken();

      if (hasRefreshToken) {
        void handleValidateSession();
      } else {
        handleCheckedAuth(false);
      }
      removeLegacySessionFromStorage();
    }
    hasFetchedUser.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only validates session on mount of the application
  }, []);

  useEffect(() => {
    const unsubscribe = ApiClient.onRefreshToken((response) => {
      if ('error' in response) {
        onRefreshTokenFailure?.();
      }
    });
    return () => void unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasCheckedAuthentication && showLoader) {
    return <LoaderComponent visible={true} />;
  }

  return children;
};

export { CoreAuthWebProvider };
